import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronLeft,
  ChevronRight,
  X,
  LifeBuoy,
  Search,
  MousePointer,
  FileText,
  Users,
  Filter,
  Info,
  AlertTriangle,
  CheckCircle,
  ArrowRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ProcessItem } from "@/types";
import { EnhancedConfetti } from "./EnhancedConfetti";
import { EnhancedSpotlightOverlay } from "./EnhancedSpotlightOverlay";
import { EnhancedTutorialTooltip } from "./EnhancedTutorialTooltip";

export interface EnhancedTutorialGuideProps {
  isOpen: boolean;
  onClose: () => void;
  processData: ProcessItem[];
  onSelectMainProcess: (title: string) => void;
  onSelectSubProcess: (title: string) => void;
  onSelectMoment: (title: string) => void;
  activeMainProcess: string;
  activeSubProcess: string;
  activeMoment: string;
}

interface TutorialStep {
  id: string;
  title: string;
  description: React.ReactNode;
  targetSelector: string;
  position: "top" | "bottom" | "left" | "right" | "center";
  icon: React.ElementType;
  spotlightOpacity?: number;
  spotlightPadding?: number;
  animation?: React.ReactNode;
}

export const EnhancedTutorialGuide: React.FC<EnhancedTutorialGuideProps> = ({
  isOpen,
  onClose,
  processData,
  onSelectMainProcess,
  onSelectSubProcess,
  onSelectMoment,
  activeMainProcess,
  activeSubProcess,
  activeMoment
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });

  // Hitta ett bra exempel från processData för demo
  const findBestExample = () => {
    // Prioritera process-items som har dokument, beskrivning och deltagare
    const goodExamples = processData.filter(item => 
      item.field_3 && // Har beskrivning
      item.field_4 && // Har dokument
      ((item.Deltagareochansvar1 && item.Deltagareochansvar1.trim() !== "") || 
       (item.Deltagareochansvar2 && item.Deltagareochansvar2.trim() !== ""))
    );
    
    // Helst ett som också har "Viktigt" och detaljerad beskrivning
    const bestExample = goodExamples.find(item => 
      item.Viktigt && 
      item.Detaljeradbeskrivning
    ) || goodExamples[0] || processData[0];
    
    return bestExample ? {
      mainProcess: bestExample.Title,
      subProcess: bestExample.field_0,
      moment: bestExample.field_2
    } : null;
  };

  const example = findBestExample();

  // Definiera tutorialstegen
  const tutorialSteps: TutorialStep[] = [
    {
      id: "welcome",
      title: "Välkommen till Process Viewer",
      description: (
        <div>
          <p className="text-base">
            Välkommen till Process Viewer! Denna guide tar dig igenom de viktigaste funktionerna i systemet.
          </p>
          <p className="mt-3 text-sm text-gray-300">
            Du kommer att få se hur du navigerar mellan processer, hittar information och använder systemets verktyg.
          </p>
        </div>
      ),
      icon: LifeBuoy,
      targetSelector: "body",
      position: "center",
      animation: (
        <div className="flex justify-center mt-4">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1, rotate: [0, 5, 0, -5, 0] }}
            transition={{ duration: 1.5, repeat: Infinity, repeatType: "reverse" }}
            className="text-orange-300"
          >
            <LifeBuoy size={48} />
          </motion.div>
        </div>
      ),
      spotlightOpacity: 0
    },
    {
      id: "search-function",
      title: "Sökfunktionen",
      description: (
        <div>
          <p className="text-base">
            Använd sökfältet för att hitta processer, moment, dokument eller deltagare i systemet.
          </p>
          <p className="mt-3 text-sm text-gray-300">
            Skriv in ett sökord så ser du matchande resultat från hela systemet. Klicka på ett resultat för att navigera direkt till det.
          </p>
        </div>
      ),
      icon: Search,
      // Uppdatera denna selector till en som matchar sökrutan i UI:t
      targetSelector: "input[placeholder*='Sök process']", // Hittar input-elementet baserat på placeholder
      position: "bottom",
      spotlightOpacity: 0.1,
      spotlightPadding: 8
    },
    {
      id: "tools-overview",
      title: "Funktioner och verktyg",
      description: (
        <div>
          <p className="text-base">
            I övre delen av skärmen hittar du systemets huvudfunktioner:
          </p>
          <div className="mt-3 space-y-2 text-sm">
            {/* ... beskrivningar av knappar ... */}
          </div>
        </div>
      ),
      icon: Info,
      // KORRIGERAD SELECTOR FÖR VERKTYGSKNAPPARNA I RÖDA BOXEN
      targetSelector: ".flex.items-center.gap-3, .header-tools", // Prova denna eller lägg till en dedikerad CSS-klass
        position: "bottom",
        spotlightOpacity: 0.1,
        spotlightPadding: 12
      },
    {
      id: "main-process",
      title: "Huvudprocesser",
      description: (
        <div>
          <p className="text-base">
            Huvudprocesserna är det översta lagret i processorganisationen. Varje huvudprocess har ett unikt nummer och namn.
          </p>
          <p className="mt-3 text-sm text-gray-300">
            Klicka på en huvudprocess för att se dess delprocesser. Aktiv process visas med orange bakgrund.
          </p>
        </div>
      ),
      icon: ChevronRight,
      targetSelector: "#main-process-navigation",
      position: "bottom",
      spotlightOpacity: 0.1,
      spotlightPadding: 8
    },
    {
      id: "sub-process",
      title: "Delprocesser",
      description: (
        <div>
          <p className="text-base">
            Delprocesser bryter ned huvudprocessen i mer specifika områden. De är numrerade med två nivåer (t.ex. 1.2).
          </p>
          <p className="mt-3 text-sm text-gray-300">
            När du väljer en delprocess uppdateras momentlistan till vänster med alla moment som ingår i delprocessen.
          </p>
        </div>
      ),
      icon: ChevronRight,
      targetSelector: "#sub-process-navigation",
      position: "bottom",
      spotlightOpacity: 0.1,
      spotlightPadding: 8
    },
    {
      id: "moment-list",
      title: "Momentlista",
      description: (
        <div>
          <p className="text-base">
            Momentlistan visar alla steg inom vald delprocess. Du kan välja kompakt eller detaljerad vy via knapparna ovanför listan.
          </p>
          <div className="mt-3 space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <ChevronRight className="h-4 w-4 text-blue-400" />
              <span><strong>Kompakt vy</strong> - Visar bara namn på moment</span>
            </div>
            <div className="flex items-center gap-2">
              <Info className="h-4 w-4 text-green-400" />
              <span><strong>Detaljerad vy</strong> - Visar även en kort beskrivning och ikoner</span>
            </div>
          </div>
        </div>
      ),
      icon: Info,
      targetSelector: "#moment-list-card",
      position: "right",
      spotlightOpacity: 0.1,
      spotlightPadding: 8
    },
    {
      id: "moment-toggle",
      title: "Byt momentvisning",
      description: (
        <div>
          <p className="text-base">
            Du kan växla mellan kompakt och detaljerad visning av momentlistan med dessa knappar.
          </p>
          <p className="mt-3 text-sm text-gray-300">
            I detaljerad vy visas mer information och ikoner som indikerar om momentet har dokument, beskrivning och viktiga noteringar.
          </p>
        </div>
      ),
      icon: Info,
      targetSelector: "#view-style-toggles",
      position: "right",
      spotlightOpacity: 0.1,
      spotlightPadding: 4
    },
    {
      id: "description-section",
      title: "Beskrivning",
      description: (
        <div>
          <p className="text-base">
            När du väljer ett moment visas detaljerad information här. Du kan se:
          </p>
          <div className="mt-3 space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <Info className="h-4 w-4 text-blue-400" />
              <span><strong>Beskrivning</strong> - Grundläggande information om momentet</span>
            </div>
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-400" />
              <span><strong>Viktigt</strong> - Kritisk information markerad i rött</span>
            </div>
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-gray-400" />
              <span><strong>Detaljerad beskrivning</strong> - Expanderbart avsnitt med mer info</span>
            </div>
          </div>
        </div>
      ),
      icon: Info,
      targetSelector: ".scroll-to-description",
      position: "left",
      spotlightOpacity: 0.1,
      spotlightPadding: 8
    },
    {
      id: "participants-section",
      title: "Deltagare och ansvar",
      description: (
        <div>
          <p className="text-base">
            Deltagarwidgeten visar alla roller som är involverade i momentet och deras respektive ansvar.
          </p>
          <div className="mt-3 space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-blue-400" />
              <span>Roller är färgkodade för enklare identifiering</span>
            </div>
            <div className="flex items-center gap-2">
              <ArrowRight className="h-4 w-4 text-orange-400" />
              <span>Om det finns alternativa tillvägagångssätt kan du jämföra dem sida vid sida</span>
            </div>
          </div>
        </div>
      ),
      icon: Users,
      targetSelector: ".scroll-to-participants",
      position: "left",
      spotlightOpacity: 0.1,
      spotlightPadding: 8
    },
    {
      id: "documents-section",
      title: "Dokument",
      description: (
        <div>
          <p className="text-base">
            Dokumentwidgeten visar alla dokument kopplade till det valda momentet.
          </p>
          <p className="mt-3 text-sm text-gray-300">
            Klicka på ett dokument för att förhandsgranska eller öppna det. Denna widget uppdateras automatiskt när du väljer olika moment.
          </p>
        </div>
      ),
      icon: FileText,
      targetSelector: "#documents-column",
      position: "left",
      spotlightOpacity: 0.1,
      spotlightPadding: 8
    },
    {
      id: "completion",
      title: "Du klarade det!",
      description: (
        <div>
          <p className="text-base">
            Grattis! Du har nu lärt dig grunderna i Process Viewer.
          </p>
          <p className="mt-3 text-sm text-gray-300">
            Du kan starta om denna guide när som helst genom att klicka på guidesymbolen i översta menyn.
          </p>
          <div className="mt-6 flex justify-center">
            <motion.div
              animate={{ scale: [1, 1.1, 1], rotate: [0, 5, 0, -5, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="inline-block"
            >
              <div className="px-4 py-2 rounded-lg bg-orange-500 text-gray-900 font-medium">
                Börja utforska!
              </div>
            </motion.div>
          </div>
        </div>
      ),
      icon: CheckCircle,
      targetSelector: "body",
      position: "center",
      spotlightOpacity: 0
    }
  ];

  // Uppdatera target-element när steg ändras eller fönsterstorlek ändras
  useEffect(() => {
    if (!isOpen) return;

    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    window.addEventListener("resize", handleResize);

    // Försök navigera till ett bra exempel-element om det behövs
    const step = tutorialSteps[currentStep];
    if (example && currentStep > 0 && currentStep < 5) {
      if (currentStep === 3 && activeMainProcess !== example.mainProcess) {
        onSelectMainProcess(example.mainProcess);
      } else if (currentStep === 4 && activeSubProcess !== example.subProcess) {
        onSelectSubProcess(example.subProcess);
      }
    }

    // Hitta element och uppdatera targetRect
    const findTargetElement = () => {
      const step = tutorialSteps[currentStep];
      if (step.targetSelector === "body") {
        setTargetRect(document.body.getBoundingClientRect());
        return;
      }

      try {
        const elements = document.querySelectorAll(step.targetSelector);
        if (elements.length > 0) {
          const element = elements[0] as HTMLElement;
          const rect = element.getBoundingClientRect();
          
          // Lägg till padding
          const padding = step.spotlightPadding || 8;
          const expandedRect = new DOMRect(
            rect.x - padding,
            rect.y - padding,
            rect.width + padding * 2,
            rect.height + padding * 2
          );
          
          setTargetRect(expandedRect);
          
          // Scrolla till elementet om det är utanför viewporten
          const buffer = 100;
          const windowHeight = window.innerHeight;
          if (rect.top < buffer || rect.bottom > windowHeight - buffer) {
            const center = rect.top + rect.height / 2;
            const scrollPos = window.scrollY + center - windowHeight / 2;
            window.scrollTo({ top: scrollPos, behavior: "smooth" });
          }
        }
      } catch (error) {
        console.warn("Element not found", error);
        setTargetRect(null);
      }
    };

    const timer = setTimeout(findTargetElement, 300);
    const updateTimer = setInterval(findTargetElement, 500);

    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(timer);
      clearInterval(updateTimer);
    };
  }, [currentStep, isOpen, windowSize, activeMainProcess, activeSubProcess, example, onSelectMainProcess, onSelectSubProcess]);

  // Gå till nästa steg
  const nextStep = () => {
    if (currentStep < tutorialSteps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      setShowConfetti(true);
      setTimeout(() => {
        onClose();
        setCurrentStep(0);
        setShowConfetti(false);
      }, 2000);
    }
  };

  // Gå till föregående steg
  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  // Avsluta guiden
  const handleClose = () => {
    if (currentStep === tutorialSteps.length - 1) {
      setShowConfetti(true);
      setTimeout(() => {
        onClose();
        setCurrentStep(0);
        setShowConfetti(false);
      }, 2000);
    } else {
      onClose();
      setCurrentStep(0);
    }
  };

  // Om guiden inte är öppen, visa ingenting
  if (!isOpen) return null;

  return (
    <>
      {/* Overlay med spotlight */}
      <EnhancedSpotlightOverlay
        targetRect={targetRect}
        onClick={nextStep}
        spotlightOpacity={tutorialSteps[currentStep].spotlightOpacity}
        spotlightPadding={tutorialSteps[currentStep].spotlightPadding}
      />

      {/* Tooltip */}
      <EnhancedTutorialTooltip
        step={tutorialSteps[currentStep]}
        currentStep={currentStep}
        totalSteps={tutorialSteps.length}
        onNext={nextStep}
        onPrev={prevStep}
        onClose={handleClose}
        tooltipRef={tooltipRef}
        targetRect={targetRect}
        position={
          targetRect && tutorialSteps[currentStep].position === "bottom" 
            ? { top: targetRect.bottom + 16, left: targetRect.left + targetRect.width / 2, position: "bottom" }
            : targetRect && tutorialSteps[currentStep].position === "top"
            ? { top: targetRect.top - 16, left: targetRect.left + targetRect.width / 2, position: "top" } 
            : targetRect && tutorialSteps[currentStep].position === "left"
            ? { top: targetRect.top + targetRect.height / 2, left: targetRect.left - 16, position: "left" }
            : targetRect && tutorialSteps[currentStep].position === "right"
            ? { top: targetRect.top + targetRect.height / 2, left: targetRect.right + 16, position: "right" }
            : { top: window.innerHeight / 2, left: window.innerWidth / 2, position: "center" }
        }
      />
      
      {/* Konfetti vid avslut */}
      {showConfetti && <EnhancedConfetti />}
    </>
  );
};

export default EnhancedTutorialGuide;